import { useState, useEffect } from "react";
import { Bell, Bug, Mail, User, CheckSquare, Calendar, LogOut, Settings, UserCircle, Lock } from "lucide-react";
import { useSubscriptionTier } from "@/hooks/useSubscriptionTier";
import { BugReportDialog } from "@/components/BugReportDialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SlideoutPanel } from "./SlideoutPanel";
import { calendarService, type ScheduleMeetingData } from "@/lib/calendarService";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { RoleSwitcher } from "@/components/RoleSwitcher";
import { messagesService, type ComposeEmailData } from "@/lib/messagesService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { showSignInControl } from "@/lib/authLoadingState";

export function AppHeader() {
  const [activePanel, setActivePanel] = useState<
    "notifications" | "messages" | "tickets" | "calendar" | null
  >(null);
  const [scheduleMeetingData, setScheduleMeetingData] =
    useState<ScheduleMeetingData | null>(null);
  const [initialComposeData, setInitialComposeData] =
    useState<ComposeEmailData | null>(null);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const { user, isAuthenticated, isLoading } = useAuth();
  const { currentRole } = useRole();
  const { hasFeature } = useSubscriptionTier();
  const queryClient = useQueryClient();

  const { data: userProfile } = useQuery<{
    agencyLogo?: string;
    agencyName?: string;
    clientLogo?: string;
    clientName?: string;
  }>({
    queryKey: ["/api/user/profile"],
    enabled: isAuthenticated,
  });

  // Notification queries are only scoped to broker/employer/employee portals
  const portalType: "broker" | "employer" | "employee" =
    currentRole === "employer" ? "employer"
    : currentRole === "employee" ? "employee"
    : "broker";

  const { data: unreadCountData } = useQuery<{ count: number }>({
    queryKey: ["/api/notifications/unread-count", { portalType }],
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });

  const unreadNotificationCount = unreadCountData?.count || 0;

  // Open (not-yet-acted-on) tasks for the broker — mirrors the notifications
  // unread badge so newly-created tickets aren't missed. Agency-scoped.
  const { data: taskStats } = useQuery<{ open: number }>({
    queryKey: ["/api/tasks/stats"],
    enabled: isAuthenticated && currentRole === "broker",
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });
  const openTaskCount = taskStats?.open || 0;

  // Role comes from useRole() above — single source of truth
  const isEmployeePortal = currentRole === "employee";
  const isEmployerPortal = currentRole === "employer";
  const isBrokerPortal = currentRole === "broker";

  // Detect roles with portal access. Use the ACCOUNT role (baseRole): while
  // viewing the employee portal the server reports the effective role
  // "employee", which used to hide the switcher / drop the Broker option and
  // strand agency members and employers in the employee portal.
  const accountRole = (user as any)?.baseRole ?? user?.role;
  const isBroker = accountRole === "broker" || accountRole === "superadmin";
  const isClient = Boolean((user as any)?.clientId);
  const isEmployer = accountRole === "employer";
  const isAgencyMember = accountRole === "agency_member";
  const hasEmployeeId = Boolean((user as any)?.employeeId);

  // Show role switcher for superadmins, employers, and agency members (with employee access).
  // Agency members without an employeeId can only access broker portal — no toggle needed.
  // Regular employees (role === "employee") should NOT see the employer portal toggle.
  const isSuperadminUser = accountRole === "superadmin" && !user?.isImpersonating;
  const agencyMemberCanSwitch = isAgencyMember && hasEmployeeId;
  const showRoleSwitcher = isSuperadminUser || isEmployer || agencyMemberCanSwitch;

  const handlePanelToggle = (
    panelType: "notifications" | "messages" | "tickets" | "calendar"
  ) => {
    // If closing the currently open panel
    if (activePanel === panelType) {
      if (activePanel === "calendar") {
        // Clear any pending schedule data when exiting calendar
        setScheduleMeetingData(null);
      }
      setActivePanel(null);
      return;
    }

    // Switching panels
    if (panelType === "calendar") {
      // User manually opened calendar, ensure stale schedule data doesn't auto-open the dialog
      setScheduleMeetingData(null);
    } else if (activePanel === "calendar") {
      // Leaving calendar to another panel, clear schedule data
      setScheduleMeetingData(null);
    }

    setActivePanel(panelType);
  };

  // Provide a close handler that also clears schedule data for calendar
  const closePanel = () => {
    if (activePanel === "calendar") {
      setScheduleMeetingData(null);
    }
    setActivePanel(null);
  };

  // Keep the panel mounted for the slide-out transition: `mountedPanel` lags
  // `activePanel` by the 300ms animation when closing, then unmounts.
  const [mountedPanel, setMountedPanel] = useState<typeof activePanel>(activePanel);
  useEffect(() => {
    if (activePanel) {
      setMountedPanel(activePanel);
      return;
    }
    const timer = setTimeout(() => setMountedPanel(null), 300);
    return () => clearTimeout(timer);
  }, [activePanel]);

  // Register callback for schedule meeting functionality
  useEffect(() => {
    calendarService.onScheduleMeeting((data: ScheduleMeetingData) => {
      setScheduleMeetingData(data);
      setActivePanel("calendar");
    });

    calendarService.onOpenCalendar(() => {
      setScheduleMeetingData(null);
      setActivePanel("calendar");
    });

    messagesService.onCompose((data: ComposeEmailData) => {
      setInitialComposeData(data);
      setActivePanel("messages");
    });

    return () => {
      calendarService.clearCallback();
      messagesService.clearCallback();
    };
  }, []);

  return (
    <>
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700 fixed w-full z-50" style={{ top: "var(--banner-offset, 0px)" }}>
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              {isEmployerPortal || isEmployeePortal ? (
                <>
                  {/* Employer/Employee Portal: Agency logo primary, then company name */}
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {userProfile?.agencyLogo ? (
                      <img
                        src={userProfile.agencyLogo}
                        alt={userProfile.agencyName || "Agency"}
                        className="h-8 w-auto max-w-[140px] object-contain"
                      />
                    ) : (
                      <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {userProfile?.agencyName || "Agency"}
                      </span>
                    )}
                    <div className="flex items-center gap-1 text-gray-400">
                      <span className="text-[10px]">powered by</span>
                      <img
                        src="/logo.png"
                        alt="Administr"
                        className="h-4 w-auto opacity-60"
                      />
                    </div>
                  </div>
                  {(userProfile?.clientLogo || userProfile?.clientName) && (
                    <>
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
                      {userProfile?.clientLogo ? (
                        <img
                          src={userProfile.clientLogo}
                          alt={userProfile.clientName || "Company"}
                          className="h-8 w-auto max-w-[140px] rounded-md object-contain"
                        />
                      ) : (
                        <span className="text-xl font-bold text-gray-800 dark:text-gray-200">
                          {userProfile.clientName}
                        </span>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* Broker Portal: Administr logo primary, then agency logo */}
                  <div className="flex-shrink-0">
                    <img
                      src="/logo.png"
                      alt="administr"
                      className="h-8 w-auto"
                    />
                  </div>
                  {userProfile?.agencyLogo && (
                    <>
                      <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
                      <img
                        src={userProfile.agencyLogo}
                        alt={userProfile.agencyName || "Agency"}
                        className="h-8 w-auto max-w-[120px] object-contain"
                      />
                    </>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {/* Role Switcher - For employers, agency members, and client admins to switch portals */}
              {showRoleSwitcher && (
                <div className="border-l border-gray-300 dark:border-gray-600 pl-4 ml-4">
                  <RoleSwitcher allowedRoles={isSuperadminUser ? ["superadmin", "broker"] : isAgencyMember ? ["broker", "employee"] : ["employer", "employee"]} />
                </div>
              )}

              <div className="hidden md:flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 dark:text-gray-400 hover:text-white hover:bg-[#1737A2] relative"
                  onClick={() => handlePanelToggle("notifications")}
                >
                  <Bell className="h-4 w-4 mr-2" />
                  Notifications
                  {unreadNotificationCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 text-[10px] flex items-center justify-center"
                    >
                      {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
                    </Badge>
                  )}
                </Button>
                {!isEmployeePortal && !isEmployerPortal && hasFeature("email_sync") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 dark:text-gray-400 hover:text-white hover:bg-[#1737A2]"
                    onClick={() => handlePanelToggle("messages")}
                    data-tour="header-messages"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Messages
                  </Button>
                )}
                {!isEmployeePortal && !isEmployerPortal && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 dark:text-gray-400 hover:text-white hover:bg-[#1737A2] relative"
                    onClick={() => handlePanelToggle("tickets")}
                    data-tour="header-tasks"
                  >
                    <CheckSquare className="h-4 w-4 mr-2" />
                    Tasks
                    {openTaskCount > 0 && (
                      <Badge
                        variant="destructive"
                        className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 text-[10px] flex items-center justify-center"
                        data-testid="header-open-task-count"
                      >
                        {openTaskCount > 99 ? "99+" : openTaskCount}
                      </Badge>
                    )}
                  </Button>
                )}
                {!isEmployeePortal && !isEmployerPortal && hasFeature("calendaring") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 dark:text-gray-400 hover:text-white hover:bg-[#1737A2]"
                    onClick={() => handlePanelToggle("calendar")}
                    data-tour="header-calendar"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Calendar
                  </Button>
                )}
              </div>

              <div className="ml-3 relative">
                {/* "Sign In" only once auth has SETTLED as signed-out. While
                    the session is still loading render nothing here — a
                    Sign In button mid-load reads as "you were logged out"
                    (BUG-008 / DEV-320). */}
                {showSignInControl({ isLoading, isAuthenticated }) ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => (window.location.href = "/api/login")}
                    className="flex items-center gap-2"
                    data-testid="header-sign-in"
                  >
                    <UserCircle className="h-4 w-4" />
                    Sign In
                  </Button>
                ) : !isAuthenticated ? null : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Avatar className="h-8 w-8 cursor-pointer">
                        {user?.profileImageUrl ? (
                          <AvatarImage
                            src={user.profileImageUrl}
                            alt="Profile"
                          />
                        ) : (
                          <AvatarFallback className="bg-[#1737A2] text-white text-sm font-medium">
                            {user?.firstName?.[0]}
                            {user?.lastName?.[0]}
                          </AvatarFallback>
                        )}
                      </Avatar>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        {user?.firstName} {user?.lastName}
                        {user?.email && (
                          <div className="text-xs text-muted-foreground font-normal">
                            {user.email}
                          </div>
                        )}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          window.location.href =
                            currentRole === "superadmin"
                              ? "/superadmin/settings?role=superadmin"
                              : `/settings?role=${currentRole}`;
                        }}
                      >
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </DropdownMenuItem>
                      {!isEmployeePortal && (
                        <DropdownMenuItem onClick={() => setBugReportOpen(true)}>
                          <Bug className="mr-2 h-4 w-4" />
                          Report a Bug
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={async () => {
                          try {
                            // Clear React Query cache
                            queryClient.clear();
                            // Clear localStorage
                            localStorage.removeItem('selectedRole');
                            // Call logout API
                            await fetch("/api/logout", { method: "POST" });
                            // Redirect to home
                            window.location.href = "/";
                          } catch (error) {
                            console.error("Logout error:", error);
                            window.location.href = "/";
                          }
                        }}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        Sign Out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Slide-out panels */}
      {mountedPanel && (
        <SlideoutPanel
          isOpen={!!activePanel}
          onClose={closePanel}
          type={mountedPanel}
          scheduleMeetingData={
            mountedPanel === "calendar" ? scheduleMeetingData : undefined
          }
          initialComposeData={
            mountedPanel === "messages" ? initialComposeData : undefined
          }
        />
      )}

      {/* Bug Report Dialog */}
      <BugReportDialog open={bugReportOpen} onOpenChange={setBugReportOpen} />
    </>
  );
}
